// Public ADHD screener funnels -> canonical analytics events (client side).
//
// The registry's original screener family was named `wlc_*`, after one customer.
// An organisation is a dimension, not a bounded context, so this generic
// `screener_*` family discriminates on a payload field (`screener_key`) and a
// third screener needs no new event names.
//
// Two things make this family different from every other client-side family:
//
//   - Every member is preAuth. These are public, sign-in-free pages, so the
//     ingest rejects any event the registry does not flag as occurring before
//     sign-in (event_ingest.py, "sign-in required for this event") and the
//     frontend classifies that rejection as permanent and drops the batch. An
//     unflagged event here is a silent total loss that looks like no traffic.
//   - The session is the screener's own funnel session, not the per-tab
//     analytics session, passed through the sessionId override on
//     emitAnalyticsEvent. That is what ties an anonymous funnel to the referral
//     it eventually produces, since the submission is recorded server-side by
//     the function that creates the record.
//
// Everything here is fire-and-forget and cannot throw into a caller: this runs
// inside a public referral form.

import { emitAnalyticsEvent } from './emitEvent';
import { MAX_STRING_LEN } from './eventRegistry';

// ── Screener keys (the discriminator; closed vocabulary) ────────────────────
// Mirrors SCREENER_KEYS in python-functions/screener_analytics.py.

// NHS Right-to-Choose referral screener: 6-item ASRS Part A gate, functional
// impairment scale, GP letter as the output (src/pages/wlc).
export const SCREENER_RTC_ASRS6 = 'rtc_asrs6';
// 18-item ASRS screener sold to several organisations, results PDF and a
// private-assessment choice as the output (python-functions/rst_functions.py).
export const SCREENER_PROVIDER_ASRS18 = 'provider_asrs18';

export const SCREENER_KEYS = [SCREENER_RTC_ASRS6, SCREENER_PROVIDER_ASRS18];

export const INSTRUMENT_ASRS_PART_A = 'asrs_part_a';

// ── Organisation attribution ────────────────────────────────────────────────
// A public page has no token, so the envelope's org_id — which is resolved from
// token claims and never client-supplied — is NULL on every screener row. The
// organisation travels in the payload as `org_label` (not `org_name`: the
// registry's PII guard bans `name` as a payload key segment, and the rule is
// worth keeping literal) beside the confidence of the claim, so no consumer can
// mistake it for authenticated attribution. Client-side emits are necessarily
// the weakest of the four values.
export const ATTRIBUTION_CLIENT_DECLARED = 'client_declared';

// ── The contract this module emits ──────────────────────────────────────────
// Declared here as well as in analytics/events.schema.json so the tests can pin
// the exact shape being reported for the registry, and so a mapping can never
// name a field this module has not deliberately proposed. Once the registry
// carries the family, the tests also check every name and field against it.

// Fields nothing can populate today are deliberately absent, even where one
// would obviously belong: a declared field that is always null reads as coverage
// in the dictionary and in the warehouse. A payload field costs nothing to add
// later - the column is JSON, so there is no migration and no backfill - so the
// honest default is to declare it in the same change that populates it.
export const SCREENER_EVENT_PAYLOADS = Object.freeze({
  screener_started: ['screener_key', 'source', 'org_label', 'org_attribution'],
  screener_step_viewed: [
    'screener_key', 'step_id', 'step_index', 'phase', 'source', 'org_label', 'org_attribution',
  ],
  screener_step_completed: [
    'screener_key', 'step_id', 'step_index', 'phase', 'source', 'org_label', 'org_attribution',
    'skipped', 'time_on_step_ms',
  ],
  screener_phase_started: [
    'screener_key', 'phase', 'step_index', 'source', 'org_label', 'org_attribution',
  ],
  screener_scored: [
    'screener_key', 'instrument', 'part_a_score', 'part_a_max', 'part_b_score', 'part_b_max',
    'total_score', 'threshold_value', 'meets_threshold', 'screening_id', 'source',
    'org_label', 'org_attribution',
  ],
  screener_submission_attempted: ['screener_key', 'source', 'org_label', 'org_attribution'],
  screener_submission_failed: ['screener_key', 'source', 'org_label', 'org_attribution'],
});

// ── Funnel-event bridge ─────────────────────────────────────────────────────
// Keyed on the funnel event names the screener's own Firestore writer already
// uses, so the bridge sits at that one writer and every existing and future
// milestone is mirrored without touching a call site.

export const SCREENER_FUNNEL_MAPPINGS = Object.freeze({
  form_start: { event: 'screener_started' },
  step_viewed: { event: 'screener_step_viewed' },
  step_completed: { event: 'screener_step_completed' },
  screening_complete: { event: 'screener_scored' },
  personal_details_started: { event: 'screener_phase_started', constants: { phase: 'personal' } },
  submission_attempt: { event: 'screener_submission_attempted' },
  submission_failed: { event: 'screener_submission_failed' },
});

// Funnel events with no counterpart, recorded so their absence is a decision.
export const UNBRIDGED_FUNNEL_EVENTS = Object.freeze({
  threshold_not_met:
    'Only the negative outcome had an event, so "threshold met" could only be counted as an '
    + 'absence. The information is meets_threshold on screener_scored, which fires either way.',
});

// Time on step is measured here rather than asked of the page: the bridge sees
// both the view and the completion of a step, so the delta needs no call-site
// change and no clock arithmetic in the warehouse (where a lost event would
// silently stretch one step's duration across two). Per page load, so a step
// completed after a refresh simply carries no duration rather than a wrong one.
const stepViewedAt = new Map();

const clampInt = (value) => (Number.isInteger(value) ? value : undefined);

const asShortString = (value) =>
  typeof value === 'string' && value ? value.slice(0, MAX_STRING_LEN) : undefined;

function assign(payload, fields) {
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined) payload[key] = value;
  });
  return payload;
}

/**
 * Mirror one screener funnel event into the analytics event stream.
 *
 * Called from the screener's existing funnel-event writer, after its own write,
 * so dedup, ordering and the "exactly once per user action" guarantee are
 * whatever that writer already provides. Returns true when an event was queued.
 *
 * @param {object} opts
 * @param {string} opts.eventName - Funnel event name (a SCREENER_FUNNEL_MAPPINGS key).
 * @param {string} opts.screenerKey - Which screener (SCREENER_KEYS).
 * @param {string} [opts.orgName] - Organisation this surface serves.
 * @param {string} [opts.source] - Route variant the person arrived through.
 * @param {string} [opts.sessionId] - The screener's own funnel session id.
 * @param {string} [opts.stepId] - Canonical step id for step events.
 * @param {string} [opts.phase] - Funnel phase label (non-PII).
 * @param {number} [opts.stepIndex] - Index within the step list.
 * @param {boolean} [opts.skipped] - An optional question advanced with no answer.
 * @param {object} [opts.scoring] - {partAScore, partAMax, threshold, meetsThreshold}
 *   for the scoring milestone. Absent scoring means no scored event is emitted:
 *   the outcome and the threshold decision are the whole value of that event.
 */
export function mirrorScreenerFunnelEvent({
  eventName,
  screenerKey,
  orgName,
  source,
  sessionId,
  stepId,
  phase,
  stepIndex,
  skipped,
  scoring,
} = {}) {
  try {
    const mapping = SCREENER_FUNNEL_MAPPINGS[eventName];
    if (!mapping || !SCREENER_KEYS.includes(screenerKey)) return false;

    const payload = assign(
      { screener_key: screenerKey },
      {
        source: asShortString(source),
        org_label: asShortString(orgName),
        org_attribution: orgName ? ATTRIBUTION_CLIENT_DECLARED : undefined,
        ...(mapping.constants || {}),
      },
    );

    const options = sessionId ? { sessionId } : undefined;

    if (mapping.event === 'screener_step_viewed') {
      if (!stepId) return false;
      stepViewedAt.set(stepId, Date.now());
      assign(payload, {
        step_id: asShortString(stepId),
        step_index: clampInt(stepIndex),
        phase: asShortString(phase),
      });
      return emitAnalyticsEvent(mapping.event, payload, options);
    }

    if (mapping.event === 'screener_step_completed') {
      if (!stepId) return false;
      const viewedAt = stepViewedAt.get(stepId);
      assign(payload, {
        step_id: asShortString(stepId),
        step_index: clampInt(stepIndex),
        phase: asShortString(phase),
        skipped: typeof skipped === 'boolean' ? skipped : undefined,
        time_on_step_ms: viewedAt === undefined ? undefined : Math.max(0, Date.now() - viewedAt),
      });
      return emitAnalyticsEvent(mapping.event, payload, options);
    }

    if (mapping.event === 'screener_phase_started') {
      assign(payload, { step_index: clampInt(stepIndex) });
      return emitAnalyticsEvent(mapping.event, payload, options);
    }

    if (mapping.event === 'screener_scored') {
      // No scores, no event: a scored row whose scores are absent would be
      // counted in the denominator and answer nothing.
      if (!scoring || !Number.isInteger(scoring.partAScore)
        || !Number.isInteger(scoring.threshold)) {
        return false;
      }
      assign(payload, {
        instrument: INSTRUMENT_ASRS_PART_A,
        part_a_score: scoring.partAScore,
        part_a_max: clampInt(scoring.partAMax),
        threshold_value: scoring.threshold,
        meets_threshold: Boolean(scoring.meetsThreshold),
      });
      return emitAnalyticsEvent(mapping.event, payload, options);
    }

    // screener_started, screener_submission_attempted, screener_submission_failed.
    return emitAnalyticsEvent(mapping.event, payload, options);
  } catch (error) {
    // Swallowed: this is called from inside a public referral form's own event
    // writer and must never be able to interrupt it.
    return false;
  }
}

// Test-only: clear the per-step view timestamps between tests.
export function __resetScreenerStepTimingForTests() {
  stepViewedAt.clear();
}
